import { fetchWithAuth } from '@/lib/api/core'

export type GdocSyncStatus = 'in_sync' | 'pull' | 'push' | 'diverged'

export type GdocSyncResult = {
  success?: boolean
  status: GdocSyncStatus
  documentId?: string
  syncedAt?: string
  pulled?: string
  pushed?: string
  localMarkdown?: string
  driveMarkdown?: string
  /** Maater document updated_at when sync compared sides */
  localModifiedTime?: string | null
  /** Google Doc modifiedTime from Drive API */
  driveModifiedTime?: string | null
  content?: string
  frontmatter?: Record<string, unknown>
  /** Present when local resolve succeeded but Drive push failed */
  pushFailed?: string
  /** True when sync auto-applied formatting-only drift (no chooser) */
  cosmeticOnly?: boolean
  error?: string
  detail?: string
}

export async function syncGoogleDoc(params: {
  documentId: string
  organizationId?: string | null
  force?: 'pull' | 'push'
  /** Menu Sync / enabling Keep synced: show chooser when bodies differ (no silent overwrite). */
  interactive?: boolean
}): Promise<GdocSyncResult> {
  const res = await fetchWithAuth('/api/google-drive/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId: params.documentId,
      organizationId: params.organizationId ?? undefined,
      force: params.force,
      interactive: params.interactive === true ? true : undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as GdocSyncResult & { error?: string }
  if (!res.ok) {
    throw new Error(body.error || body.detail || `Sync failed (HTTP ${res.status})`)
  }
  return body
}

export async function resolveGoogleDocSync(params: {
  documentId: string
  organizationId?: string | null
  resolvedMarkdown: string
  alsoPush?: boolean
}): Promise<GdocSyncResult> {
  const res = await fetchWithAuth('/api/google-drive/sync/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId: params.documentId,
      organizationId: params.organizationId ?? undefined,
      resolvedMarkdown: params.resolvedMarkdown,
      alsoPush: params.alsoPush ?? false,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as GdocSyncResult & { error?: string }
  if (!res.ok) {
    throw new Error(body.error || body.detail || `Resolve failed (HTTP ${res.status})`)
  }
  return body
}

export async function registerGoogleDocWatch(params: {
  documentId: string
  fileId: string
  organizationId?: string | null
}): Promise<{ watchRegistered: boolean; degraded?: boolean; reason?: string }> {
  const res = await fetchWithAuth('/api/google-drive/watch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId: params.documentId,
      fileId: params.fileId,
      organizationId: params.organizationId ?? undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as {
    watchRegistered?: boolean
    degraded?: boolean
    reason?: string
    error?: string
  }
  if (!res.ok) {
    // Unexpected transport/auth failure only
    console.warn('[gdoc-sync] watch register failed:', body.error || `HTTP ${res.status}`)
    return { watchRegistered: false, degraded: true, reason: body.error }
  }
  return {
    watchRegistered: body.watchRegistered !== false && !body.degraded,
    degraded: Boolean(body.degraded),
    reason: body.reason,
  }
}

export async function stopGoogleDocWatch(documentId: string): Promise<void> {
  const res = await fetchWithAuth('/api/google-drive/watch', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentId }),
  })
  if (!res.ok) {
    console.warn('[gdoc-sync] watch stop failed')
  }
}

export async function updateGdocSyncModeFrontmatter(params: {
  documentId: string
  mode: 'manual' | 'keep_synced'
  currentFrontmatter?: Record<string, unknown> | null
}): Promise<Record<string, unknown>> {
  const { updateDocument } = await import('@/lib/api/documents')
  const next = {
    ...(params.currentFrontmatter || {}),
    gdoc_sync_mode: params.mode,
  }
  await updateDocument(params.documentId, { frontmatter: next })
  return next
}

export type LinkedGoogleDocument = {
  id: string
  title: string
  gdocId: string
  filePath?: string | null
  updatedAt?: string | null
  organizationId?: string | null
  gdocSyncMode?: string | null
  gdocLastSynced?: string | null
}

export type PublishGoogleDocResult = {
  success: boolean
  fileId: string
  webViewLink: string
  title?: string
  frontmatter?: Record<string, unknown>
  pushed?: string
  error?: string
  detail?: string
  hint?: string
}

/** Create a Drive folder under parentFolderId (`root` = My Drive root). */
export async function createGoogleDriveFolder(params: {
  name: string
  parentFolderId: string
  organizationId?: string | null
}): Promise<{ id: string; name: string }> {
  const res = await fetchWithAuth('/api/google-drive/folders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: params.name,
      parentFolderId: params.parentFolderId,
      organizationId: params.organizationId ?? undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as {
    folder?: { id?: string; name?: string }
    error?: string
    detail?: string
  }
  if (!res.ok || !body.folder?.id) {
    throw new Error(body.detail || body.error || `Create folder failed (HTTP ${res.status})`)
  }
  return { id: body.folder.id, name: body.folder.name || params.name }
}

/** Remove Drive link from a Maater doc (Google Doc stays in Drive). */
export async function unlinkDocumentFromGoogleDrive(params: {
  documentId: string
  organizationId?: string | null
}): Promise<{
  frontmatter: Record<string, unknown>
  unlinkedFileId: string
  webViewLink?: string
}> {
  const res = await fetchWithAuth('/api/google-drive/unlink', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId: params.documentId,
      organizationId: params.organizationId ?? undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as {
    frontmatter?: Record<string, unknown>
    unlinkedFileId?: string
    webViewLink?: string
    error?: string
    detail?: string
  }
  if (!res.ok) {
    throw new Error(body.detail || body.error || `Unlink failed (HTTP ${res.status})`)
  }
  return {
    frontmatter: body.frontmatter || {},
    unlinkedFileId: body.unlinkedFileId || '',
    webViewLink: body.webViewLink,
  }
}

/** Create a new Google Doc from a Maater document (no gdoc_id yet). */
export async function publishDocumentToGoogleDrive(params: {
  documentId: string
  parentFolderId: string
  organizationId?: string | null
}): Promise<PublishGoogleDocResult> {
  const res = await fetchWithAuth('/api/google-drive/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId: params.documentId,
      parentFolderId: params.parentFolderId,
      organizationId: params.organizationId ?? undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as PublishGoogleDocResult
  if (!res.ok) {
    const err = new Error(
      body.detail || body.error || `Publish failed (HTTP ${res.status})`,
    ) as Error & {
      hint?: string
      status?: number
    }
    err.hint = body.hint || (res.status === 409 ? body.detail : undefined)
    err.status = res.status
    throw err
  }
  return body
}

/** Maater docs with frontmatter.gdoc_id (Drive sidebar Linked + Activity discovery). */
export async function fetchLinkedGoogleDocuments(params?: {
  /** Single-org filter (legacy). Prefer organizationIds for multi-workspace. */
  organizationId?: string | null
  /** Active workspace IDs - lists linked docs across these orgs. */
  organizationIds?: string[] | null
  /** mine (default) = docs this user linked; all = org-wide team view */
  scope?: 'mine' | 'all'
  limit?: number
}): Promise<LinkedGoogleDocument[]> {
  const qs = new URLSearchParams()
  const multi = (params?.organizationIds || []).filter(Boolean)
  if (multi.length > 0) {
    qs.set('workspaceIds', multi.join(','))
  } else if (params?.organizationId) {
    qs.set('organizationId', params.organizationId)
  }
  // When neither is set, API returns linked docs for all accessible orgs.
  if (params?.scope === 'all') {
    qs.set('scope', 'all')
  }
  if (params?.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const res = await fetchWithAuth(`/api/google-drive/linked-documents${suffix}`)
  const body = (await res.json().catch(() => ({}))) as {
    documents?: LinkedGoogleDocument[]
    error?: string
  }
  if (!res.ok) {
    throw new Error(body.error || `Failed to load linked documents (HTTP ${res.status})`)
  }
  return body.documents ?? []
}

/** One Google file can be linked as multiple Maater docs - Activity fetches comments per gdocId. */
export function uniqueLinkedDocsByGdocId<T extends { gdocId?: string | null }>(docs: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const doc of docs) {
    const id = typeof doc.gdocId === 'string' ? doc.gdocId.trim() : ''
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(doc)
  }
  return out
}

export function uniqueDriveCommentsByFile<T extends { id?: string; fileId: string }>(
  comments: T[],
): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const comment of comments) {
    const key = `${comment.fileId}:${comment.id ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(comment)
  }
  return out
}
